import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useNotification } from '@asabdev/asab-ui';
import { modelGatewayApi } from '@/features/model-gateway/api';
import { getModelOperationError } from '@/features/model-gateway/utils/errorHandling';
import type {
  BaseModelResponse,
  CustomModelResponse,
  AsabModelResponse,
  CreateCustomModelRequest,
  UpdateCustomModelRequest,
  CreateAsabModelRequest,
  UpdateAsabModelRequest,
  Provider,
  BaseModelQueryParams,
  CustomModelQueryParams,
  AsabModelQueryParams,
} from '@/features/model-gateway/types';

/**
 * Model Gateway Store
 * Manages state for base models and custom models
 */
export const useModelGatewayStore = defineStore('modelGateway', () => {
  // Notification composable
  const { showSuccess, showError } = useNotification();

  // State
  const baseModels = ref<BaseModelResponse[]>([]);
  const customModels = ref<CustomModelResponse[]>([]);
  const asabModels = ref<AsabModelResponse[]>([]);
  const isLoadingBaseModels = ref(false);
  const isLoadingCustomModels = ref(false);
  const isLoadingAsabModels = ref(false);
  const baseModelsError = ref<string | null>(null);
  const customModelsError = ref<string | null>(null);
  const asabModelsError = ref<string | null>(null);

  // Getters
  const enabledBaseModels = computed(() =>
    baseModels.value.filter(model => model.is_enabled_for_org)
  );

  const baseModelsByProvider = computed(
    () => (provider: Provider) => baseModels.value.filter(model => model.provider === provider)
  );

  const getBaseModelBySlug = computed(
    () => (slug: string) => baseModels.value.find(model => model.slug === slug)
  );

  const getCustomModelBySlug = computed(
    () => (slug: string) => customModels.value.find(model => model.slug === slug)
  );

  const getAsabModelBySlug = computed(
    () => (slug: string) => asabModels.value.find(model => model.slug === slug)
  );

  // Helper functions
  const clearBaseModelsError = () => {
    baseModelsError.value = null;
  };

  const clearCustomModelsError = () => {
    customModelsError.value = null;
  };

  const clearAsabModelsError = () => {
    asabModelsError.value = null;
  };

  // === Base Model Actions ===

  /**
   * Fetch all base models with optional filtering
   * @param params - Optional query parameters for filtering by provider or enabled status
   */
  const fetchBaseModels = async (params?: BaseModelQueryParams): Promise<void> => {
    try {
      isLoadingBaseModels.value = true;
      clearBaseModelsError();

      const response = await modelGatewayApi.listBaseModels(params);
      const previousAddedMap = new Map(
        baseModels.value.map(model => [model.slug, model.is_added_to_org ?? false])
      );
      baseModels.value = response.data.map(model => {
        const previouslyAdded = previousAddedMap.get(model.slug) ?? false;
        const inferredAdded = model.is_enabled_for_org ? true : previouslyAdded;
        return {
          ...model,
          is_added_to_org: model.is_added_to_org ?? inferredAdded,
        };
      });
    } catch (err) {
      const errorResult = getModelOperationError(err, 'fetch', 'base model');
      baseModelsError.value = errorResult.message;
      showError(errorResult.message);
    } finally {
      isLoadingBaseModels.value = false;
    }
  };

  /**
   * Add a base model to the organization with optional custom name
   * @param slug - The unique identifier for the base model
   * @param name - Optional custom name for the model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const addBaseModel = async (slug: string, name?: string): Promise<boolean> => {
    try {
      isLoadingBaseModels.value = true;
      clearBaseModelsError();

      await modelGatewayApi.addBaseModel({ base_model_slug: slug, name });

      // Update the model in the local state
      const modelIndex = baseModels.value.findIndex(model => model.slug === slug);
      if (modelIndex !== -1) {
        baseModels.value[modelIndex] = {
          ...baseModels.value[modelIndex],
          is_enabled_for_org: true,
          is_added_to_org: true,
        };
      }

      showSuccess('Base model added to organization successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'add', 'base model');
      baseModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingBaseModels.value = false;
    }
  };

  /**
   * Enable a base model for the organization
   * @param name - The name of the base model (custom name or slug)
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const enableBaseModel = async (name: string): Promise<boolean> => {
    try {
      isLoadingBaseModels.value = true;
      clearBaseModelsError();

      await modelGatewayApi.enableBaseModel(name);

      // Update the model in the local state
      const modelIndex = baseModels.value.findIndex(
        model => model.slug === name || model.name === name
      );
      if (modelIndex !== -1) {
        baseModels.value[modelIndex] = {
          ...baseModels.value[modelIndex],
          is_enabled_for_org: true,
          is_added_to_org: true,
        };
      }

      showSuccess('Base model enabled successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'enable', 'base model');
      baseModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingBaseModels.value = false;
    }
  };

  /**
   * Disable a base model for the organization
   * @param name - The name of the base model (custom name or slug)
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const disableBaseModel = async (name: string): Promise<boolean> => {
    try {
      isLoadingBaseModels.value = true;
      clearBaseModelsError();

      await modelGatewayApi.disableBaseModel(name);

      // Update the model in the local state
      const modelIndex = baseModels.value.findIndex(
        model => model.slug === name || model.name === name
      );
      if (modelIndex !== -1) {
        baseModels.value[modelIndex] = {
          ...baseModels.value[modelIndex],
          is_enabled_for_org: false,
          is_added_to_org: true,
        };
      }

      showSuccess('Base model disabled successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'disable', 'base model');
      baseModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingBaseModels.value = false;
    }
  };

  /**
   * Remove a base model from the organization
   * @param name - The name of the base model (custom name or slug)
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const deleteBaseModel = async (name: string): Promise<boolean> => {
    try {
      isLoadingBaseModels.value = true;
      clearBaseModelsError();

      await modelGatewayApi.deleteBaseModel(name);

      // Update the model in the local state
      const modelIndex = baseModels.value.findIndex(
        model => model.slug === name || model.name === name
      );
      if (modelIndex !== -1) {
        baseModels.value[modelIndex] = {
          ...baseModels.value[modelIndex],
          is_enabled_for_org: false,
          is_added_to_org: false,
        };
      }

      showSuccess('Base model removed from organization successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'delete', 'base model');
      baseModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingBaseModels.value = false;
    }
  };

  // === Custom Model Actions ===

  /**
   * Fetch all custom models with optional filtering
   * @param params - Optional query parameters for filtering by enabled status
   */
  const fetchCustomModels = async (params?: CustomModelQueryParams): Promise<void> => {
    try {
      isLoadingCustomModels.value = true;
      clearCustomModelsError();

      const response = await modelGatewayApi.listCustomModels(params);
      customModels.value = response.data;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'fetch', 'custom model');
      customModelsError.value = errorResult.message;
      showError(errorResult.message);
    } finally {
      isLoadingCustomModels.value = false;
    }
  };

  /**
   * Create a new custom model
   * @param data - The custom model configuration
   * @returns Promise that resolves to the created model if successful, null otherwise
   */
  const createCustomModel = async (
    data: CreateCustomModelRequest
  ): Promise<CustomModelResponse | null> => {
    try {
      isLoadingCustomModels.value = true;
      clearCustomModelsError();

      const newModel = await modelGatewayApi.createCustomModel(data);

      // Add the new model to the local state
      customModels.value.unshift(newModel);

      showSuccess('Custom model created successfully!');
      return newModel;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'create', 'custom model');
      customModelsError.value = errorResult.message;
      showError(errorResult.message);
      return null;
    } finally {
      isLoadingCustomModels.value = false;
    }
  };

  /**
   * Update an existing custom model
   * @param slug - The unique identifier for the custom model
   * @param data - The updated custom model configuration
   * @returns Promise that resolves to the updated model if successful, null otherwise
   */
  const updateCustomModel = async (
    slug: string,
    data: UpdateCustomModelRequest
  ): Promise<CustomModelResponse | null> => {
    try {
      isLoadingCustomModels.value = true;
      clearCustomModelsError();

      const updatedModel = await modelGatewayApi.updateCustomModel(slug, data);

      // Update the model in the local state
      const modelIndex = customModels.value.findIndex(model => model.slug === slug);
      if (modelIndex !== -1) {
        customModels.value[modelIndex] = updatedModel;
      }

      showSuccess('Custom model updated successfully!');
      return updatedModel;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'update', 'custom model');
      customModelsError.value = errorResult.message;
      showError(errorResult.message);
      return null;
    } finally {
      isLoadingCustomModels.value = false;
    }
  };

  /**
   * Enable a custom model
   * @param slug - The unique identifier for the custom model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const enableCustomModel = async (slug: string): Promise<boolean> => {
    try {
      isLoadingCustomModels.value = true;
      clearCustomModelsError();

      const updatedModel = await modelGatewayApi.enableCustomModel(slug);

      // Update the model in the local state
      const modelIndex = customModels.value.findIndex(model => model.slug === slug);
      if (modelIndex !== -1) {
        customModels.value[modelIndex] = updatedModel;
      }

      showSuccess('Custom model enabled successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'enable', 'custom model');
      customModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingCustomModels.value = false;
    }
  };

  /**
   * Disable a custom model
   * @param slug - The unique identifier for the custom model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const disableCustomModel = async (slug: string): Promise<boolean> => {
    try {
      isLoadingCustomModels.value = true;
      clearCustomModelsError();

      const updatedModel = await modelGatewayApi.disableCustomModel(slug);

      // Update the model in the local state
      const modelIndex = customModels.value.findIndex(model => model.slug === slug);
      if (modelIndex !== -1) {
        customModels.value[modelIndex] = updatedModel;
      }

      showSuccess('Custom model disabled successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'disable', 'custom model');
      customModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingCustomModels.value = false;
    }
  };

  /**
   * Delete a custom model
   * @param slug - The unique identifier for the custom model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const deleteCustomModel = async (slug: string): Promise<boolean> => {
    try {
      isLoadingCustomModels.value = true;
      clearCustomModelsError();

      await modelGatewayApi.deleteCustomModel(slug);

      // Remove the model from the local state
      customModels.value = customModels.value.filter(model => model.slug !== slug);

      showSuccess('Custom model deleted successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'delete', 'custom model');
      customModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingCustomModels.value = false;
    }
  };

  // === ASAB Model Actions ===

  /**
   * Fetch all ASAB models with optional filtering
   * @param params - Optional query parameters for filtering by enabled status
   */
  const fetchAsabModels = async (params?: AsabModelQueryParams): Promise<void> => {
    try {
      isLoadingAsabModels.value = true;
      clearAsabModelsError();

      const response = await modelGatewayApi.listAsabModels(params);
      asabModels.value = response.data;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'fetch', 'ASAB model');
      asabModelsError.value = errorResult.message;
      showError(errorResult.message);
    } finally {
      isLoadingAsabModels.value = false;
    }
  };

  /**
   * Create a new ASAB model
   * @param data - The ASAB model configuration
   * @returns Promise that resolves to the created model if successful, null otherwise
   */
  const createAsabModel = async (
    data: CreateAsabModelRequest
  ): Promise<AsabModelResponse | null> => {
    try {
      isLoadingAsabModels.value = true;
      clearAsabModelsError();

      const newModel = await modelGatewayApi.createAsabModel(data);

      // Add the new model to the local state
      asabModels.value.unshift(newModel);

      showSuccess('ASAB model created successfully!');
      return newModel;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'create', 'ASAB model');
      asabModelsError.value = errorResult.message;
      showError(errorResult.message);
      return null;
    } finally {
      isLoadingAsabModels.value = false;
    }
  };

  /**
   * Update an existing ASAB model
   * @param slug - The unique identifier for the ASAB model
   * @param data - The updated ASAB model configuration
   * @returns Promise that resolves to the updated model if successful, null otherwise
   */
  const updateAsabModel = async (
    slug: string,
    data: UpdateAsabModelRequest
  ): Promise<AsabModelResponse | null> => {
    try {
      isLoadingAsabModels.value = true;
      clearAsabModelsError();

      const updatedModel = await modelGatewayApi.updateAsabModel(slug, data);

      // Update the model in the local state
      const modelIndex = asabModels.value.findIndex(model => model.slug === slug);
      if (modelIndex !== -1) {
        asabModels.value[modelIndex] = updatedModel;
      }

      showSuccess('ASAB model updated successfully!');
      return updatedModel;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'update', 'ASAB model');
      asabModelsError.value = errorResult.message;
      showError(errorResult.message);
      return null;
    } finally {
      isLoadingAsabModels.value = false;
    }
  };

  /**
   * Enable an ASAB model
   * @param slug - The unique identifier for the ASAB model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const enableAsabModel = async (slug: string): Promise<boolean> => {
    try {
      isLoadingAsabModels.value = true;
      clearAsabModelsError();

      const updatedModel = await modelGatewayApi.enableAsabModel(slug);

      // Update the model in the local state
      const modelIndex = asabModels.value.findIndex(model => model.slug === slug);
      if (modelIndex !== -1) {
        asabModels.value[modelIndex] = updatedModel;
      }

      showSuccess('ASAB model enabled successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'enable', 'ASAB model');
      asabModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingAsabModels.value = false;
    }
  };

  /**
   * Disable an ASAB model
   * @param slug - The unique identifier for the ASAB model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const disableAsabModel = async (slug: string): Promise<boolean> => {
    try {
      isLoadingAsabModels.value = true;
      clearAsabModelsError();

      const updatedModel = await modelGatewayApi.disableAsabModel(slug);

      // Update the model in the local state
      const modelIndex = asabModels.value.findIndex(model => model.slug === slug);
      if (modelIndex !== -1) {
        asabModels.value[modelIndex] = updatedModel;
      }

      showSuccess('ASAB model disabled successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'disable', 'ASAB model');
      asabModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingAsabModels.value = false;
    }
  };

  /**
   * Delete an ASAB model
   * @param slug - The unique identifier for the ASAB model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const deleteAsabModel = async (slug: string): Promise<boolean> => {
    try {
      isLoadingAsabModels.value = true;
      clearAsabModelsError();

      await modelGatewayApi.deleteAsabModel(slug);

      // Remove the model from the local state
      asabModels.value = asabModels.value.filter(model => model.slug !== slug);

      showSuccess('ASAB model deleted successfully!');
      return true;
    } catch (err) {
      const errorResult = getModelOperationError(err, 'delete', 'ASAB model');
      asabModelsError.value = errorResult.message;
      showError(errorResult.message);
      return false;
    } finally {
      isLoadingAsabModels.value = false;
    }
  };

  /**
   * Reset the store to its initial state
   */
  const resetStore = () => {
    baseModels.value = [];
    customModels.value = [];
    asabModels.value = [];
    isLoadingBaseModels.value = false;
    isLoadingCustomModels.value = false;
    isLoadingAsabModels.value = false;
    baseModelsError.value = null;
    customModelsError.value = null;
    asabModelsError.value = null;
  };

  return {
    // State
    baseModels,
    customModels,
    asabModels,
    isLoadingBaseModels,
    isLoadingCustomModels,
    isLoadingAsabModels,
    baseModelsError,
    customModelsError,
    asabModelsError,

    // Getters
    enabledBaseModels,
    baseModelsByProvider,
    getBaseModelBySlug,
    getCustomModelBySlug,
    getAsabModelBySlug,

    // Actions
    fetchBaseModels,
    addBaseModel,
    enableBaseModel,
    disableBaseModel,
    deleteBaseModel,
    fetchCustomModels,
    createCustomModel,
    updateCustomModel,
    enableCustomModel,
    disableCustomModel,
    deleteCustomModel,
    fetchAsabModels,
    createAsabModel,
    updateAsabModel,
    enableAsabModel,
    disableAsabModel,
    deleteAsabModel,
    clearBaseModelsError,
    clearCustomModelsError,
    clearAsabModelsError,
    resetStore,
  };
});
